import CustomToast from '../../../../../components/Toast';
import { downloadCSV } from '../../../../assignments/utils';
import { DateFormatter } from '../../../../../utils/DateFormatter';
import { convertRowsToCSV } from '../../../../../utils/convertRowsToCSV';

const calculateChangePercent = (metric) => {
  if (
    metric?.previous !== 0 &&
    metric?.previous !== null &&
    metric?.previous !== undefined
  ) {
    return ((metric.current - metric.previous) / metric.previous) * 100;
  }

  return 0;
};

const getDateRangeText = (selectedFilters) => {
  if (
    selectedFilters?.isCustomRange &&
    selectedFilters?.startDateUtc &&
    selectedFilters?.endDateUtc
  ) {
    return `${DateFormatter.toShortDate2(selectedFilters.startDateUtc)} to ${DateFormatter.toShortDate2(selectedFilters.endDateUtc)}`;
  }

  return selectedFilters?.dateRangeName || 'N/A';
};

const getMetricRow = (metricName, metric, yearOverYearComparison) => {
  if (yearOverYearComparison) {
    return [
      metricName,
      metric?.current ?? 0,
      metric?.previous ?? 0,
      calculateChangePercent(metric).toFixed(1)
    ];
  }

  return [metricName, metric?.current ?? 0];
};

const getReportHeaderRows = ({
  reportData,
  selectedFilters,
  yearOverYearComparison,
  ferpaEnabled
}) => [
  ['Companion App Usage Report'],
  [],
  ['District', selectedFilters?.districtName || 'All Districts'],
  ['School', selectedFilters?.schoolName || 'All Schools'],
  ['Teacher', selectedFilters?.teacherName || 'All Teachers'],
  ['Student', ferpaEnabled ? selectedFilters?.studentName || 'All Students' : 'name hidden'],
  ['Date Range', getDateRangeText(selectedFilters)],
  ['Year-over-Year Comparison', yearOverYearComparison ? 'On' : 'Off'],
  ['Report Run Date', reportData?.reportRunUtc ? DateFormatter.toLongDateTime2(reportData.reportRunUtc).replace(',', ' ') : '-']
];

const getCoreMetricRows = (dataSummary, yearOverYearComparison) => [
  yearOverYearComparison ? ['Metric', 'Current', 'Previous', 'Change (%)'] : ['Metric', 'Current'],
  getMetricRow('Daily Active Users', dataSummary?.dailyActiveUsers, yearOverYearComparison),
  getMetricRow('Monthly Active Users', dataSummary?.monthlyActiveUsers, yearOverYearComparison),
  getMetricRow('Avg Session Length (min)', dataSummary?.averageSessionLength, yearOverYearComparison),
  getMetricRow('Sessions Per User', dataSummary?.sessionsPerUser, yearOverYearComparison)
];

const getActiveUserTrendRows = (reportData, yearOverYearComparison) => [
  ['Daily And Monthly Active Users Trend'],
  yearOverYearComparison
    ? ['Date', 'Daily Active Users', '', 'Monthly Active Users', '']
    : ['Date', 'Daily Active Users', 'Monthly Active Users'],
  yearOverYearComparison
    ? ['', 'Current Year', 'Previous Year', 'Current Year', 'Previous Year']
    : ['', 'Current Year', 'Current Year'],
  ...(reportData?.activeUsersTrend?.dailyActiveUsers ?? []).map((dailyItem, index) => {
    const monthlyItem = reportData?.activeUsersTrend?.monthlyActiveUsers?.[index];

    return yearOverYearComparison
      ? [
        dailyItem.date?.split('T')[0] || '',
        dailyItem.currentYearValue ?? 0,
        dailyItem.previousYearValue ?? 0,
        monthlyItem?.currentYearValue ?? 0,
        monthlyItem?.previousYearValue ?? 0
      ]
      : [
        dailyItem.date?.split('T')[0] || '',
        dailyItem.currentYearValue ?? 0,
        monthlyItem?.currentYearValue ?? 0
      ];
  })
];

const getFeatureUsageRows = (featureUsage) => [
  ['Feature Usage'],
  ['Feature', 'Usage Count'],
  ...featureUsage.map((item) => [item.featureName, item.usageCount ?? 0])
];

const getRetentionRateRows = (reportData, yearOverYearComparison) => [
  ['Retention Rate (%)'],
  yearOverYearComparison ? ['', 'Day1', 'Day7', 'Day30'] : ['Day1', 'Day7', 'Day30'],
  ...(yearOverYearComparison
    ? [
      [
        'Current',
        reportData?.retentionRate?.currentPeriod?.day1Percent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day7Percent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day30Percent ?? 0,
      ],
      [
        'Change',
        reportData?.retentionRate?.currentPeriod?.day1ChangePercent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day7ChangePercent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day30ChangePercent ?? 0,
      ],
    ]
    : [
      [
        reportData?.retentionRate?.currentPeriod?.day1Percent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day7Percent ?? 0,
        reportData?.retentionRate?.currentPeriod?.day30Percent ?? 0,
      ],
    ])
];

const getRetentionTrendRows = (reportData) => [
  ['Week', 'Day1', 'Day7', 'Day30'],
  ...(Array.isArray(reportData?.retentionRate?.trendData)
    ? reportData.retentionRate.trendData.map((data) => [
      data?.weekLabel ?? '',
      data?.day1Percent ?? 0,
      data?.day7Percent ?? 0,
      data?.day30Percent ?? 0,
    ])
    : [])
];

const getDataSummaryRows = (dataSummary, yearOverYearComparison) => [
  ['Data Summary'],
  yearOverYearComparison ? ['Metric', 'Current', 'Previous', 'Change (%)'] : ['Metric', 'Current'],
  getMetricRow('Daily Active Users', dataSummary?.dailyActiveUsers, yearOverYearComparison),
  getMetricRow('Monthly Active Users', dataSummary?.monthlyActiveUsers, yearOverYearComparison),
  getMetricRow('Average Session Length', dataSummary?.averageSessionLength, yearOverYearComparison),
  getMetricRow('Sessions Per User', dataSummary?.sessionsPerUser, yearOverYearComparison),
  getMetricRow('Day 1 Retention', dataSummary?.day1Retention, yearOverYearComparison),
  getMetricRow('Day 7 Retention', dataSummary?.day7Retention, yearOverYearComparison),
  getMetricRow('Day 30 Retention', dataSummary?.day30Retention, yearOverYearComparison)
];

const getUsageRankingRows = ({ reportData, ferpaEnabled }) => [
  ['Total Usage Ranking'],
  ['Top Districts'],
  ['Rank', 'District Name', 'Session/User', 'Avg Session (min)', 'Engagement Score'],
  ...(reportData?.topUsageRankings?.districts?.map((item) => [
    item.rank,
    item.name,
    item.sessionsPerUser ?? 0,
    item.avgSessionMinutes ?? 0,
    item.engagementScore ?? 0
  ]) || []),
  [],
  ['Top Schools'],
  ['Rank', 'School Name', 'Session/User', 'Avg Session (min)', 'Engagement Score'],
  ...(reportData?.topUsageRankings?.schools?.map((item) => [
    item.rank,
    item.name,
    item.sessionsPerUser ?? 0,
    item.avgSessionMinutes ?? 0,
    item.engagementScore ?? 0
  ]) || []),
  [],
  ['Top Students'],
  ['Rank', 'Student Name', 'Session/User', 'Avg Session (min)', 'Engagement Score'],
  ...(reportData?.topUsageRankings?.students?.map((item) => [
    item.rank,
    ferpaEnabled ? item.name : 'name hidden',
    item.sessionsPerUser ?? 0,
    item.avgSessionMinutes ?? 0,
    item.engagementScore ?? 0
  ]) || [])
];

const getCsvRows = ({
  reportData,
  selectedFilters,
  yearOverYearComparison,
  ferpaEnabled
}) => {
  const dataSummary = reportData?.dataSummary;
  const featureUsage = reportData?.featureUsage?.features ?? [];

  return [
    ...getReportHeaderRows({ reportData, selectedFilters, yearOverYearComparison, ferpaEnabled }),
    [],
    ...getCoreMetricRows(dataSummary, yearOverYearComparison),
    [],
    ...getActiveUserTrendRows(reportData, yearOverYearComparison),
    [],
    ...getFeatureUsageRows(featureUsage),
    [],
    ...getRetentionRateRows(reportData, yearOverYearComparison),
    [],
    ...getRetentionTrendRows(reportData),
    [],
    ...getDataSummaryRows(dataSummary, yearOverYearComparison),
    [],
    ...getUsageRankingRows({ reportData, ferpaEnabled })
  ];
};

export const exportCsvReport = ({
  reportData,
  selectedFilters,
  yearOverYearComparison,
  ferpaEnabled
}) => {
  try {
    if (!reportData) {
      CustomToast('No report data to export', 'error');
      return;
    }

    const csvRows = getCsvRows({
      reportData,
      selectedFilters,
      yearOverYearComparison,
      ferpaEnabled
    });

    const csvContent = convertRowsToCSV(csvRows);
    downloadCSV(csvContent, 'Companion_App_Usage_Report');

    CustomToast('Report exported successfully', 'success');
  } catch (error) {
    CustomToast(error?.message || 'Failed to export report', 'error');
  }
};